"use client";

import type { ReactNode } from "react";

import { isVideo } from "@/components/shared/media/image-format";
import MarkdownCaption from "@/components/shared/media/MarkdownCaption";
import ImageWithLightbox from "@/components/shared/media/ImageWithLightbox";

type ImageLayout =
  | "proud"
  | "twins"
  | "shy"
  | "keep-ratio"
  | "aligned"
  | "twins-aligned"
  | "keep-ratio-aligned";

export interface FigureProps {
  src: string;
  width: number;
  height: number;
  alt: string;
  caption?: ReactNode;
  layout?: ImageLayout;
  documentation?: boolean;
  priority?: boolean;
}

export default function Figure({
  src,
  width,
  height,
  alt,
  caption,
  layout = "proud",
  documentation,
  priority,
}: FigureProps) {
  const displayWidth = Math.max(1, Math.floor(width / 2));
  const displayHeight = Math.max(
    1,
    Math.round((displayWidth / width) * height),
  );
  const sizes =
    layout === "shy"
      ? "(min-width: 1024px) 33vw, 100vw"
      : layout === "aligned"
        ? "(min-width: 1024px) 50vw, 100vw"
        : "(min-width: 1024px) 67vw, 100vw";

  return (
    <section
      data-image
      data-layout={layout}
      className={
        layout === "aligned"
          ? documentation
            ? "col-span-full mt-4 mb-10 lg:mt-10"
            : "blog-paragraph-wrapper mt-4 mb-10 lg:mt-10"
          : layout === "shy"
            ? "col-span-full mt-4 mb-10 lg:col-start-3 lg:col-span-4 lg:mt-10"
            : "col-span-full mt-4 mb-10 lg:mt-10 lg:grid lg:grid-cols-8"
      }
    >
      <figure
        className="col-span-full mx-auto flex w-full flex-col items-center justify-center gap-2.5 overflow-hidden rounded-lg"
        style={{ maxWidth: "100%", width: displayWidth }}
      >
        {isVideo(src) ? (
          // Screen recordings ship as MP4 instead of GIF, which is ~10x smaller.
          <video
            src={src}
            aria-label={alt}
            width={displayWidth}
            height={displayHeight}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="block h-auto w-full max-w-full rounded-lg border border-white/8"
          />
        ) : (
          <ImageWithLightbox
            url={src}
            alt={alt}
            width={displayWidth}
            height={displayHeight}
            fullSizeWidth={width}
            fullSizeHeight={height}
            sizes={sizes}
            priority={priority}
            wrapperClassName="block w-full"
            imageClassName="my-0! h-auto w-full max-w-full rounded-lg border border-white/8"
          />
        )}
        {caption ? (
          <figcaption className="italic" style={{ maxWidth: displayWidth }}>
            {typeof caption === "string" ? (
              <MarkdownCaption>{caption}</MarkdownCaption>
            ) : (
              caption
            )}
          </figcaption>
        ) : null}
      </figure>
    </section>
  );
}
